import { useEffect, useState, useCallback } from "react";
import { Typography, Link, ListItem } from "@mui/material";
import Grid from '@mui/material/Grid';
import ItemCards from "./ItemCards";
import { IMember } from "../types/interfaces/IMember";
import { IMemberAddress } from "../types/interfaces/IMemberAddress";
import { DisplayParams } from "../types/classes/DisplayParams";
import { AapColors } from '../../global/styles/AapColors';

interface ItemProps {
    member: IMember;
    index: number;
    displayParams: DisplayParams;
    onDisplayChange: (displayParams: DisplayParams) => void;
}


/**
 * Item Component
 * 
 * This component displays a result item and handles the toggle of item detail (cards).
 * It shows the member list items with alternate background colors.
 * 
 * Props:
 * - member: The member object containing the member's information.
 * - index: The index of the member item in the list.
 * - displayParams: An object of type DisplayParams containing the current display parameters.
 * - onDisplayChange: A function to handle the display change event.
 * 
 * Example usage:
 * ```tsx
 * <Item member={member} index={index} displayParams={displayParams} onDisplayChange={handleDisplayChange} />
 * ```
 * 
 * @param {IMember} member - The member object containing the member's information.
 * @param {number} index - The index of the member item in the list.
 * @param {DisplayParams} displayParams - The current display parameters.
 * @param {(displayParams: DisplayParams) => void} onDisplayChange - The function to handle the display change event.
 * @returns {JSX.Element} The component displaying the member item with details toggle.
 */
const Item: React.FC<ItemProps> = ({ member, index, displayParams, onDisplayChange }) => {
    if (member == null) return (<></>);
    const address: IMemberAddress | null = member.Addresses !== null && member.Addresses !== undefined ? member.Addresses[0] : null;

    const [show, setShow] = useState(displayParams.ShowDetailFlag);

    const handleOnClick = useCallback(() => {
        if (displayParams.ShowDetailFlag) {
            const newDisplayParams: DisplayParams = new DisplayParams();
            newDisplayParams.Page = displayParams.Page;
            newDisplayParams.ShowDetailFlag = false;
            onDisplayChange(newDisplayParams);
        }
        setShow(prev => !prev);
    }, [displayParams, onDisplayChange]);

    useEffect(() => {
        if (displayParams.ShowDetailFlag) {
            setShow(false);
        }
    }, [displayParams, displayParams.ShowDetailFlag]);

    //show member list items with alternate background colors.
    function GetDirectoryItem() {
        const alternatingItemStyle = index % 2 ? { bgcolor: AapColors.AapBackground } : {};

        return (
            <>
                <ListItem sx={alternatingItemStyle}>
                    <Grid 
                        container
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            alignContent: 'flex-start',
                        }}
                        rowSpacing={1}
                        columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item sx={{ display: "flex" }} xs={12} sm={6} md={6}>
                            <Link sx={{ textDecoration: "underline" }} component="button" onClick={handleOnClick}>
                                {member.Name}
                            </Link>
                        </Grid>
                        <Grid item sx={{ display: "flex" }} xs={12} sm={6} md={2}>
                            {address && <Typography color="secondary.dark">{address.City}</Typography>}
                        </Grid>
                        <Grid item sx={{ display: "flex", paddingLeft: 1 }} xs={12} sm={6} md={2}>
                            {address && <Typography color="secondary.dark">{address.State}</Typography>}
                        </Grid>
                        <Grid item sx={{ display: "flex" }} xs={12} sm={6} md={2}>
                            {address && <Typography color="secondary.dark">{member.Phone?.split(',')[0].trim()}</Typography>}
                        </Grid>
                    </Grid>
                </ListItem>
                <ListItem sx={alternatingItemStyle}>
                    {!displayParams.ShowDetailFlag && show &&
                        <ItemCards member={member} />}
                </ListItem>
            </>
        );
    }

    return GetDirectoryItem();
};

export default Item;